// Native SVG export for the React Flow flowchart canvas.
//
// html-to-image's toSvg() wraps HTML in <foreignObject>, which Visio,
// Illustrator, Inkscape and PowerPoint cannot read. Instead this walks the
// rendered canvas and re-emits it as plain SVG primitives:
//   - edges: the connector path React Flow already computed (flow coords)
//   - nodes: HTML boxes -> <rect>/<line>, text -> <text> (one per rendered
//     line, using the browser's own line breaks), inline icons -> nested <svg>
// Everything is measured from the live DOM, so the file matches the screen
// (floating edges, waypoints, badges, overflow chips) without duplicating
// each node component's layout rules here.

import { buildDrawioModel, escapeXml } from './buildDrawioModel.ts';
import type { BadgeKind, ExportBadge, ExportEdge, ExportNode } from './buildDrawioModel.ts';

interface FlowRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ParsedColor {
  color: string;
  alpha: number;
}

interface Projector {
  zoom: number;
  rect(r: { left: number; top: number; width: number; height: number }): FlowRect;
}

interface BoundsTracker {
  add(r: FlowRect | null | undefined): void;
  get(): FlowRect;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
const EXPORT_PADDING = 40;
const DEFAULT_EDGE_STROKE = '#000000';
const DEFAULT_EDGE_WIDTH = 2;
// React Flow's default marker size when an edge marker has no width/height.
const DEFAULT_MARKER_SIZE = 12.5;

// Interactive-only chrome that should never appear in an exported diagram.
const SKIP_SELECTOR = [
  '.react-flow__handle',
  '.react-flow__resize-control',
  '.easy-connect-source',
  '[data-export-ignore]',
].join(',');

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

// Parses "rgb(a)" computed colors; returns null for fully transparent.
export function parseColor(value: string | null | undefined): ParsedColor | null {
  if (!value || value === 'transparent') return null;
  const m = value.match(/rgba?\(([^)]+)\)/);
  if (!m) return { color: value, alpha: 1 };
  const parts = m[1].split(/[\s,/]+/).filter(Boolean).map(Number);
  const [r, g, b] = parts;
  const alpha = parts.length > 3 ? parts[3] : 1;
  if (!(alpha > 0)) return null;
  return { color: `rgb(${r},${g},${b})`, alpha };
}

export function fillAttrs(parsed: ParsedColor | null, prefix = 'fill'): string {
  if (!parsed) return `${prefix}="none"`;
  const opacity = parsed.alpha < 1 ? ` ${prefix}-opacity="${round(parsed.alpha)}"` : '';
  return `${prefix}="${parsed.color}"${opacity}`;
}

// checkVisibility() is newer than this TypeScript's DOM lib.
type VisibilityCheckable = Element & {
  checkVisibility?: (opts: { opacityProperty?: boolean; visibilityProperty?: boolean }) => boolean;
};

function isVisible(el: Element): boolean {
  const checkable = el as VisibilityCheckable;
  if (typeof checkable.checkVisibility === 'function') {
    return checkable.checkVisibility({ opacityProperty: true, visibilityProperty: true });
  }
  const cs = window.getComputedStyle(el);
  return cs.display !== 'none' && cs.visibility !== 'hidden' && Number(cs.opacity) !== 0;
}

// Maps screen rects into flow coordinates using the viewport transform.
function createProjector(viewportEl: Element): Projector {
  const matrix = new DOMMatrixReadOnly(window.getComputedStyle(viewportEl).transform);
  const zoom = matrix.a || 1;
  const origin = viewportEl.getBoundingClientRect();
  return {
    zoom,
    rect(r) {
      return {
        x: (r.left - origin.left) / zoom,
        y: (r.top - origin.top) / zoom,
        width: r.width / zoom,
        height: r.height / zoom,
      };
    },
  };
}

// Paint level for an element: the highest numeric z-index on it or any
// ancestor inside the node, so badges positioned above the node body
// (z-40/z-50) paint on top in the export too. The walk passes the parent's
// level down, so each element's style is read once.
function stackLevel(cs: CSSStyleDeclaration, parentLevel: number): number {
  const z = Number.parseInt(cs.zIndex, 10);
  return Number.isFinite(z) && z > parentLevel ? z : parentLevel;
}

function createBoundsTracker(): BoundsTracker {
  const b = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  return {
    add(r) {
      if (!r || !(r.width >= 0) || !(r.height >= 0)) return;
      b.minX = Math.min(b.minX, r.x);
      b.minY = Math.min(b.minY, r.y);
      b.maxX = Math.max(b.maxX, r.x + r.width);
      b.maxY = Math.max(b.maxY, r.y + r.height);
    },
    get() {
      if (!Number.isFinite(b.minX)) return { x: 0, y: 0, width: 0, height: 0 };
      return { x: b.minX, y: b.minY, width: b.maxX - b.minX, height: b.maxY - b.minY };
    },
  };
}

export function renderBox(cs: CSSStyleDeclaration, r: FlowRect): string {
  const bg = parseColor(cs.backgroundColor);
  const sides = ['Top', 'Right', 'Bottom', 'Left'].map((side) => ({
    side,
    width: Number.parseFloat(cs.getPropertyValue(`border-${side.toLowerCase()}-width`)) || 0,
    color: cs.getPropertyValue(`border-${side.toLowerCase()}-style`) === 'none'
      ? null
      : parseColor(cs.getPropertyValue(`border-${side.toLowerCase()}-color`)),
  }));
  const hasBorder = sides.some((s) => s.width > 0 && s.color);
  if (!bg && !hasBorder) return '';

  const radius = Math.min(
    Number.parseFloat(cs.borderTopLeftRadius) || 0,
    r.width / 2,
    r.height / 2
  );
  const uniform = sides.every((s) => s.width === sides[0].width
    && (s.color?.color || null) === (sides[0].color?.color || null));
  const out: string[] = [];

  if (uniform && hasBorder) {
    // SVG strokes are centered on the edge; inset by half the width so the
    // outer edge lines up with the CSS border box.
    const w = sides[0].width;
    out.push(
      `<rect x="${round(r.x + w / 2)}" y="${round(r.y + w / 2)}" width="${round(Math.max(0, r.width - w))}" height="${round(Math.max(0, r.height - w))}"`
      + ` rx="${round(Math.max(0, radius - w / 2))}" ${fillAttrs(bg)} ${fillAttrs(sides[0].color, 'stroke')} stroke-width="${round(w)}"/>`
    );
    return out.join('');
  }

  if (bg) {
    out.push(`<rect x="${round(r.x)}" y="${round(r.y)}" width="${round(r.width)}" height="${round(r.height)}" rx="${round(radius)}" ${fillAttrs(bg)}/>`);
  }
  sides.forEach(({ side, width, color }) => {
    if (!(width > 0) || !color) return;
    const h = width / 2;
    const line: number[] = ({
      Top: [r.x, r.y + h, r.x + r.width, r.y + h],
      Right: [r.x + r.width - h, r.y, r.x + r.width - h, r.y + r.height],
      Bottom: [r.x, r.y + r.height - h, r.x + r.width, r.y + r.height - h],
      Left: [r.x + h, r.y, r.x + h, r.y + r.height],
    } as Record<string, number[]>)[side];
    out.push(`<line x1="${round(line[0])}" y1="${round(line[1])}" x2="${round(line[2])}" y2="${round(line[3])}" ${fillAttrs(color, 'stroke')} stroke-width="${round(width)}"/>`);
  });
  return out.join('');
}

// Nearest ancestor that visually clips its content (overflow hidden or a
// -webkit-line-clamp), bounded by the node root.
function findClip(el: Element, root: Element): { el: Element; clamp: number | null } | null {
  for (let cur: Element | null = el; cur && cur !== root.parentElement; cur = cur.parentElement) {
    const cs = window.getComputedStyle(cur);
    const clamp = Number.parseInt(cs.getPropertyValue('-webkit-line-clamp'), 10);
    if (Number.isFinite(clamp) && clamp > 0) return { el: cur, clamp };
    if (cs.overflow === 'hidden' || cs.overflowY === 'hidden') return { el: cur, clamp: null };
  }
  return null;
}

// Splits a text node into the lines the browser actually rendered by
// measuring each word's client rect and grouping by vertical position.
function measureTextLines(textNode: Text): Array<{ text: string; rect: { left: number; top: number; width: number; height: number } }> {
  const text = textNode.textContent || '';
  const range = document.createRange();
  const lines: Array<{ start: number; end: number; rect: DOMRect; right: number }> = [];
  const wordRe = /\S+/g;
  let match: RegExpExecArray | null;
  while ((match = wordRe.exec(text)) !== null) {
    range.setStart(textNode, match.index);
    range.setEnd(textNode, match.index + match[0].length);
    const rects = Array.from(range.getClientRects()).filter((rc) => rc.width > 0 || rc.height > 0);
    if (rects.length === 0) continue;
    const first = rects[0];
    const current = lines[lines.length - 1];
    if (current && Math.abs(current.rect.top - first.top) < first.height / 2) {
      current.end = match.index + match[0].length;
      current.right = Math.max(current.right, first.right);
    } else {
      lines.push({ start: match.index, end: match.index + match[0].length, rect: first, right: first.right });
    }
  }
  range.detach();
  return lines.map((line) => ({
    text: text.slice(line.start, line.end).replace(/\s+/g, ' '),
    rect: { left: line.rect.left, top: line.rect.top, width: line.right - line.rect.left, height: line.rect.height },
  }));
}

function renderText(textNode: Text, root: Element, project: Projector): string {
  const parent = textNode.parentElement;
  if (!parent || !(textNode.textContent || '').trim()) return '';
  const cs = window.getComputedStyle(parent);
  let lines = measureTextLines(textNode);
  if (lines.length === 0) return '';

  const clip = findClip(parent, root);
  let truncated = false;
  if (clip) {
    if (clip.clamp && lines.length > clip.clamp) {
      lines = lines.slice(0, clip.clamp);
      truncated = true;
    }
    const box = clip.el.getBoundingClientRect();
    const kept = lines.filter((l) => l.rect.top >= box.top - 1 && l.rect.top + l.rect.height <= box.bottom + 1);
    if (kept.length < lines.length) truncated = true;
    lines = kept;
  }
  if (lines.length === 0) return '';
  if (truncated) {
    const last = lines[lines.length - 1];
    last.text = `${last.text.replace(/[\s.…]+$/, '')}…`;
  }

  const fontSize = Number.parseFloat(cs.fontSize) || 12;
  const color = parseColor(cs.color) || { color: '#000000', alpha: 1 };
  const attrs = [
    `font-family="${escapeXml(cs.fontFamily)}"`,
    `font-size="${round(fontSize)}"`,
    cs.fontWeight && cs.fontWeight !== '400' ? `font-weight="${escapeXml(cs.fontWeight)}"` : '',
    cs.fontStyle === 'italic' ? 'font-style="italic"' : '',
    fillAttrs(color),
  ].filter(Boolean).join(' ');

  return lines.map((line) => {
    const r = project.rect(line.rect);
    // Baseline sits roughly 0.35em below the vertical middle of the glyph box
    // for the system UI fonts this app uses.
    const baseline = r.y + r.height / 2 + fontSize * 0.35;
    return `<text x="${round(r.x)}" y="${round(baseline)}" ${attrs}>${escapeXml(line.text)}</text>`;
  }).join('');
}

// Inline icons/shapes (risk triangles, hexagons, cylinders, folders, lucide
// icons, the decision diamond and start/end ovals) are already SVG: re-anchor
// a clone as a nested <svg> at the same flow position.
function renderInlineSvg(svgEl: SVGSVGElement, project: Projector): string {
  const r = project.rect(svgEl.getBoundingClientRect());
  if (!(r.width > 0) || !(r.height > 0)) return '';
  const clone = svgEl.cloneNode(true) as SVGSVGElement;
  // Drop drop-shadow filters: their ids collide across nodes and many
  // editors render them poorly.
  clone.querySelectorAll('filter').forEach((f) => f.remove());
  clone.querySelectorAll('[filter]').forEach((n) => n.removeAttribute('filter'));
  clone.querySelectorAll('defs').forEach((d) => { if (!d.children.length) d.remove(); });
  ['class', 'style', 'aria-hidden'].forEach((a) => clone.removeAttribute(a));
  Array.from(clone.attributes)
    .filter((a) => a.name.startsWith('data-'))
    .forEach((a) => clone.removeAttribute(a.name));
  clone.setAttribute('x', String(round(r.x)));
  clone.setAttribute('y', String(round(r.y)));
  clone.setAttribute('width', String(round(r.width)));
  clone.setAttribute('height', String(round(r.height)));
  clone.setAttribute('overflow', 'visible');
  // Lucide icons stroke with currentColor; pin it to the rendered color.
  const color = parseColor(window.getComputedStyle(svgEl).color);
  if (color) clone.setAttribute('color', color.color);
  clone.removeAttribute('xmlns');
  return new XMLSerializer().serializeToString(clone).replace(/ xmlns="[^"]*"/, '');
}

// Walks a node (or edge label) element and returns paint items in DOM order.
function collectElementItems(root: Element, project: Projector, bounds: BoundsTracker): string {
  const items: Array<{ level: number; markup: string }> = [];
  const walk = (el: Element, parentLevel: number): void => {
    if (el.matches(SKIP_SELECTOR)) return;
    if (!isVisible(el)) return;
    if (el.tagName === 'BUTTON' && /^Delete/i.test(el.getAttribute('title') || '')) return;

    const cs = window.getComputedStyle(el);
    const level = stackLevel(cs, parentLevel);
    if (el instanceof SVGSVGElement) {
      const markup = renderInlineSvg(el, project);
      if (markup) {
        items.push({ level, markup });
        bounds.add(project.rect(el.getBoundingClientRect()));
      }
      return;
    }

    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      const r = project.rect(rect);
      const box = renderBox(cs, r);
      if (box) {
        items.push({ level, markup: box });
        bounds.add(r);
      }
    }

    el.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const markup = renderText(child as Text, root, project);
        if (markup) items.push({ level, markup });
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        walk(child as Element, level);
      }
    });
  };
  walk(root, 0);
  // Stable sort keeps DOM order within the same stacking level.
  return items
    .map((item, i) => ({ ...item, i }))
    .sort((a, b) => (a.level - b.level) || (a.i - b.i))
    .map((item) => item.markup)
    .join('');
}

const BADGE_KINDS: ReadonlySet<string> = new Set<BadgeKind>([
  'risk', 'control', 'mapped-control', 'system', 'document', 'overflow',
]);
const SHAPE_SELECTOR = 'path,polygon,rect,ellipse,circle';

export function toHex(parsed: ParsedColor | null): string | undefined {
  if (!parsed) return undefined;
  const m = parsed.color.match(/^rgb\((\d+),(\d+),(\d+)\)$/);
  if (!m) return parsed.color;
  return `#${m.slice(1, 4).map((v) => Number(v).toString(16).padStart(2, '0')).join('')}`;
}

// Visible text of an inline SVG badge, one entry per <text>, without the
// <title> tooltips nested inside them.
function svgBadgeText(svgEl: SVGSVGElement): string[] {
  return Array.from(svgEl.querySelectorAll('text'))
    .map((t) => Array.from(t.childNodes)
      .filter((n) => n.nodeType === Node.TEXT_NODE)
      .map((n) => n.textContent || '')
      .join('')
      .trim())
    .filter(Boolean);
}

// Reads the [data-export-badge] markers on a step so the draw.io model can
// recreate them as native shapes (position, colors, text) on that step.
function collectBadges(nodeEl: Element, nodeId: string, project: Projector): ExportBadge[] {
  const badges: ExportBadge[] = [];
  nodeEl.querySelectorAll('[data-export-badge]').forEach((el) => {
    const kind = el.getAttribute('data-export-badge') || '';
    if (!BADGE_KINDS.has(kind) || !isVisible(el)) return;
    const tooltip = el.getAttribute('data-export-label') || undefined;
    const svgEl = el instanceof SVGSVGElement ? el : el.querySelector('svg');

    if (!svgEl) {
      // HTML chip (e.g. "+3"): take box and colors from computed style.
      const cs = window.getComputedStyle(el);
      badges.push({
        nodeId,
        kind: kind as BadgeKind,
        rect: project.rect(el.getBoundingClientRect()),
        text: (el.textContent || '').trim(),
        tooltip,
        fill: toHex(parseColor(cs.backgroundColor)),
        stroke: toHex(parseColor(cs.borderTopColor)),
        fontColor: toHex(parseColor(cs.color)),
      });
      return;
    }

    // The badge's box is the union of its drawn shapes, not the (padded)
    // SVG viewport, so a hexagon inside a 70x70 icon maps to its real size.
    const shapes = Array.from(svgEl.querySelectorAll(SHAPE_SELECTOR));
    const rects = (shapes.length ? shapes : [svgEl]).map((n) => n.getBoundingClientRect());
    const left = Math.min(...rects.map((r) => r.left));
    const top = Math.min(...rects.map((r) => r.top));
    const right = Math.max(...rects.map((r) => r.right));
    const bottom = Math.max(...rects.map((r) => r.bottom));
    const primary = shapes[0];
    const textEl = svgEl.querySelector('text');
    const lines = svgBadgeText(svgEl);
    const count = Number(el.getAttribute('data-export-count'));

    badges.push({
      nodeId,
      kind: kind as BadgeKind,
      rect: project.rect({ left, top, width: right - left, height: bottom - top }),
      text: kind === 'risk' && count > 1 ? `${lines.join(' ')}${count}` : lines.join('\n'),
      tooltip,
      fill: primary?.getAttribute('fill') || undefined,
      stroke: primary?.getAttribute('stroke') || undefined,
      fontColor: textEl?.getAttribute('fill') || undefined,
      isKey: lines.includes('KEY'),
    });
  });
  return badges;
}

export interface MarkerSpec {
  id: string;
  color: string;
  width: number;
  height: number;
}

// Resolves a React Flow edge marker (object with color/width/height, or a
// bare type string) to the arrowhead the canvas draws for it.
export function markerSpec(marker: unknown, fallbackColor: string): MarkerSpec {
  const m = marker && typeof marker === 'object'
    ? (marker as { color?: unknown; width?: unknown; height?: unknown })
    : {};
  const color = typeof m.color === 'string' && m.color ? m.color : fallbackColor;
  const width = Number(m.width) > 0 ? Number(m.width) : DEFAULT_MARKER_SIZE;
  const height = Number(m.height) > 0 ? Number(m.height) : DEFAULT_MARKER_SIZE;
  const id = `arrow-${color.replace(/[^a-zA-Z0-9]/g, '')}-${width}x${height}`.replace(/\./g, '_');
  return { id, color, width, height };
}

function renderEdges(
  container: Element,
  edges: ExportEdge[] | null | undefined,
  bounds: BoundsTracker
): { defs: string; markup: string } {
  const edgeById = new Map((edges || []).map((e) => [String(e.id), e]));
  const markerSpecs = new Map<string, MarkerSpec>();
  const paths: string[] = [];

  container.querySelectorAll('.react-flow__edge').forEach((group) => {
    const id = group.getAttribute('data-id')
      || (group.getAttribute('data-testid') || '').replace(/^rf__edge-/, '');
    const edge = edgeById.get(String(id));
    if (!edge || edge.hidden) return;
    const pathEl = group.querySelector<SVGPathElement>('path.react-flow__edge-path');
    const d = pathEl?.getAttribute('d');
    if (!pathEl || !d) return;

    // Use the edge's configured style, not the live one, so a selected
    // (red) connector exports in its normal color.
    const stroke = edge.style?.stroke || DEFAULT_EDGE_STROKE;
    const width = edge.style?.strokeWidth || DEFAULT_EDGE_WIDTH;
    const dash = edge.style?.strokeDasharray;
    let markers = '';
    if (edge.markerEnd) {
      const spec = markerSpec(edge.markerEnd, stroke);
      markerSpecs.set(spec.id, spec);
      markers += ` marker-end="url(#${spec.id})"`;
    }
    if (edge.markerStart) {
      const spec = markerSpec(edge.markerStart, stroke);
      markerSpecs.set(spec.id, spec);
      markers += ` marker-start="url(#${spec.id})"`;
    }
    paths.push(
      `<path d="${escapeXml(d)}" fill="none" stroke="${escapeXml(stroke)}" stroke-width="${round(Number(width) || DEFAULT_EDGE_WIDTH)}"`
      + `${dash ? ` stroke-dasharray="${escapeXml(dash)}"` : ''}${markers}/>`
    );
    try {
      const bb = pathEl.getBBox();
      bounds.add({ x: bb.x, y: bb.y, width: bb.width, height: bb.height });
    } catch (_) {
      // getBBox throws for detached/non-rendered paths; bounds come from nodes.
    }
  });

  // Mirrors React Flow's ArrowClosed marker (same viewBox, size and
  // stroke-scaled units as the canvas).
  const defs = Array.from(markerSpecs.values()).map((m) => (
    `<marker id="${m.id}" viewBox="-10 -10 20 20" markerWidth="${m.width}" markerHeight="${m.height}" refX="0" refY="0"`
    + ` orient="auto-start-reverse" markerUnits="strokeWidth">`
    + `<polyline points="-5,-4 0,0 -5,4 -5,-4" fill="${escapeXml(m.color)}" stroke="${escapeXml(m.color)}" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>`
    + '</marker>'
  )).join('');

  return { defs, markup: paths.join('') };
}

/**
 * Builds a standalone SVG document from the rendered React Flow canvas.
 * `container` is the element wrapping <ReactFlow>; `nodes`/`edges` are the
 * React Flow state (used for hidden flags, edge style and markers). The root
 * `content` attribute carries a draw.io model so draw.io opens the file as an
 * editable diagram.
 */
export function buildFlowchartSvg({ container, nodes, edges, title }: {
  container: Element | null | undefined;
  nodes: ExportNode[] | null | undefined;
  edges: ExportEdge[] | null | undefined;
  title?: string;
}): string {
  const viewportEl = container?.querySelector('.react-flow__viewport');
  if (!container || !viewportEl) throw new Error('Flowchart canvas is not rendered');

  const project = createProjector(viewportEl);
  const bounds = createBoundsTracker();
  const nodeById = new Map((nodes || []).map((n) => [String(n.id), n]));

  const nodeEls = Array.from(container.querySelectorAll('.react-flow__node'))
    .filter((el) => {
      const node = nodeById.get(String(el.getAttribute('data-id')));
      return node && !node.hidden;
    });
  // Swimlanes are the background; everything else sits above the connectors.
  const isSwimlane = (el: Element): boolean => nodeById.get(String(el.getAttribute('data-id')))?.type === 'swimlane';
  const laneMarkup = nodeEls.filter(isSwimlane).map((el) => collectElementItems(el, project, bounds)).join('');
  const nodeMarkup = nodeEls.filter((el) => !isSwimlane(el)).map((el) => collectElementItems(el, project, bounds)).join('');

  const { defs, markup: edgeMarkup } = renderEdges(container, edges, bounds);

  const edgeById = new Map((edges || []).map((e) => [String(e.id), e]));
  const labelMarkup = Array.from(container.querySelectorAll('[data-export-edge-label]'))
    .filter((el) => {
      const edge = edgeById.get(String(el.getAttribute('data-export-edge-label')));
      return edge && !edge.hidden && String(edge.data?.label || '').trim();
    })
    .map((el) => collectElementItems(el, project, bounds))
    .join('');

  const b = bounds.get();
  const x = round(b.x - EXPORT_PADDING);
  const y = round(b.y - EXPORT_PADDING);
  const width = round(b.width + EXPORT_PADDING * 2);
  const height = round(b.height + EXPORT_PADDING * 2);

  const badges = nodeEls
    .filter((el) => !isSwimlane(el))
    .flatMap((el) => collectBadges(el, String(el.getAttribute('data-id')), project));
  const drawioModel = buildDrawioModel({ nodes, edges, name: title, badges });

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<svg xmlns="${SVG_NS}" content="${escapeXml(drawioModel)}" width="${width}" height="${height}" viewBox="${x} ${y} ${width} ${height}">`,
    title ? `<title>${escapeXml(title)}</title>` : '',
    defs ? `<defs>${defs}</defs>` : '',
    `<rect x="${x}" y="${y}" width="${width}" height="${height}" fill="#ffffff"/>`,
    `<g id="swimlanes">${laneMarkup}</g>`,
    `<g id="connectors">${edgeMarkup}</g>`,
    `<g id="steps">${nodeMarkup}</g>`,
    `<g id="connector-labels">${labelMarkup}</g>`,
    '</svg>',
  ].join('\n');
}

export function downloadSvg(svgMarkup: string, filename: string): void {
  const blob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
